import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  HostBinding,
  Injector,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MakeProvider } from '../abstract-value-accessor/abstract-value-accessor';
import { FileInputBasicComponent } from '../file-input-basic/file-input-basic.component';

@Component({
  selector: 'app-multiple-file-input',
  templateUrl: './multiple-file-input.component.html',
  styleUrls: ['./multiple-file-input.component.scss'],
  providers: [MakeProvider(MultipleFileInputComponent)],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MultipleFileInputComponent extends FileInputBasicComponent implements OnChanges, OnInit {
  @ViewChild('multipleFileUploadField')
  private multipleFileUploadField: ElementRef<HTMLInputElement>;

  @ViewChild('singleFileUploadField')
  private singleFileUploadField: ElementRef<HTMLInputElement>;

  @HostBinding('class.has-value')
  get hasValue(): boolean {
    return !!this.filesCount;
  }

  @Input()
  errorTexts: Record<string, string | (() => string)>;

  @Input()
  errorInfoIconTexts: Record<string, string>;

  @Input()
  required: boolean;

  @Input()
  label: string;

  @Input()
  index: number;

  @Input()
  validateOnDirty = false;

  @Input()
  labelIcon: string;

  @Input()
  displayFilesSeparated = false;

  @Input()
  shouldRemoveFileValidation?: () => PromiseLike<boolean>;

  @Input()
  acceptedFileExtensions?: string[];

  @ContentChild('selectFilesTrigger')
  protected selectFilesTrigger: TemplateRef<any>;

  selectedFileIndex: number | null = null;

  get acceptedFileExtensionsString(): string | undefined {
    if (!this.acceptedFileExtensions) {
      return;
    }

    return this.acceptedFileExtensions.join(',');
  }

  get filesCount(): number {
    return this.valueAsFileArray?.length || 0;
  }

  get fileNamePlaceholder(): string {
    if (this.valueAsFileArray.length === 1) {
      return this.valueAsFileArray[0].name;
    }

    return this.buildTranslationKey('selectedFilesPlaceholder');
  }

  constructor(private cd: ChangeDetectorRef, injector: Injector) {
    super(injector);
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    super.ngOnChanges(simpleChanges);
  }

  isFileTypeValid(fileIndex: number): boolean {
    const { errors } = this.formControl;

    if (errors && 'fileTypeError' in errors) {
      return !(errors.fileTypeError as Set<number>).has(fileIndex);
    }

    return true;
  }

  changeMultipleFiles(): void {
    if (this.displayFilesSeparated && this.hasValue) {
      this.addFilesFromInput();
    } else {
      this.setFilesFromInput();
    }
  }

  changeSingleFile(): void {
    if (this.singleFileUploadField.nativeElement.files.length) {
      const newValue = [...this.valueAsFileArray];

      newValue[this.selectedFileIndex] = this.singleFileUploadField.nativeElement.files[0];
      this.value = newValue;
    }
  }

  openAddFilesModal(): void {
    if (this.isDisabled) {
      return;
    }

    this.multipleFileUploadField.nativeElement.click();
  }

  openChangeFileModal(fileIndex: number): void {
    this.selectedFileIndex = fileIndex;
    this.singleFileUploadField.nativeElement.click();
  }

  async removeFile(fileInputIndex: number): Promise<void> {
    let shouldBeRemoved = true;

    if (this.shouldRemoveFileValidation) {
      shouldBeRemoved = await this.shouldRemoveFileValidation();
    }

    if (shouldBeRemoved) {
      const newFiles = [...this.valueAsFileArray];

      newFiles.splice(fileInputIndex, 1);
      this.value = newFiles;
      this.cd.detectChanges();
    }
  }

  buildTranslationKey(relativeKey: string): string {
    return `openedPlugin.dynamicForm.${relativeKey}`;
  }

  protected checkIsValueEqual(valueToCheck: File[]): boolean {
    if (valueToCheck.length !== this._value?.length) {
      return false;
    }

    for (let i = 0; i < valueToCheck.length; i++) {
      if (valueToCheck[i] !== this._value[i]) {
        return false;
      }
    }

    return true;
  }

  private setFilesFromInput(): void {
    this.value = this.multipleFileUploadField.nativeElement.files;
  }

  private addFilesFromInput(): void {
    const newFiles = this.multipleFileUploadField.nativeElement.files;
    this.value = [...this.valueAsFileArray, ...Array.from(newFiles)];
  }
}
